<template>
  <div class="pagination-wrapper">
    <div class="pagination-container">
      <!-- 左侧：分页信息 -->
      <div class="pagination-info">
        <div class="info-badge">
          <svg
            class="info-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span class="info-text">
            <span class="info-range">{{ startItem }}-{{ endItem }}</span>
            <span class="info-divider">/</span>
            <span class="info-total">{{ total }}</span>
          </span>
        </div>
      </div>

      <!-- 中间：分页控件 -->
      <div class="pagination-controls">
        <!-- 上一页 -->
        <button
          @click="
            (e) => {
              createRipple(e);
              goToPrevious();
            }
          "
          :disabled="currentPage === 1"
          class="nav-btn prev-btn"
          :class="{ disabled: currentPage === 1 }"
          title="上一页"
        >
          <svg
            class="nav-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2.5"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span class="nav-ripple"></span>
        </button>

        <!-- 页码 -->
        <div class="page-list">
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="
              (e) => {
                if (page !== '...') createRipple(e);
                goToPage(page);
              }
            "
            class="page-btn"
            :class="{
              active: page === currentPage,
              ellipsis: page === '...',
            }"
            :disabled="page === '...'"
          >
            <span class="page-number">{{ page }}</span>
            <span class="page-glow"></span>
            <span v-if="page === currentPage" class="active-bar"></span>
            <span v-if="page === currentPage" class="active-pulse"></span>
          </button>
        </div>

        <!-- 下一页 -->
        <button
          @click="
            (e) => {
              createRipple(e);
              goToNext();
            }
          "
          :disabled="currentPage === totalPages"
          class="nav-btn next-btn"
          :class="{ disabled: currentPage === totalPages }"
          title="下一页"
        >
          <svg
            class="nav-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2.5"
              d="M9 5l7 7-7 7"
            />
          </svg>
          <span class="nav-ripple"></span>
        </button>
      </div>

      <!-- 右侧：快速跳转 -->
      <div class="quick-jump">
        <span class="jump-label">跳至</span>
        <div class="jump-group">
          <input
            v-model.number="jumpPage"
            @keyup.enter="handleJump"
            type="number"
            min="1"
            :max="totalPages"
            class="jump-input"
            placeholder="页"
          />
          <button
            @click="
              (e) => {
                createRipple(e);
                handleJump();
              }
            "
            class="jump-btn"
            title="跳转"
          >
            <svg
              class="jump-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";

export default {
  name: "Pagination",
  props: {
    total: {
      type: Number,
      required: true,
    },
    pageSize: {
      type: Number,
      default: 5,
    },
    modelValue: {
      type: Number,
      default: 1,
    },
  },
  emits: ["update:modelValue", "change"],
  setup(props, { emit }) {
    const currentPage = ref(props.modelValue);
    const jumpPage = ref("");

    const totalPages = computed(() => Math.ceil(props.total / props.pageSize));

    const startItem = computed(
      () => (currentPage.value - 1) * props.pageSize + 1
    );
    const endItem = computed(() =>
      Math.min(currentPage.value * props.pageSize, props.total)
    );

    const visiblePages = computed(() => {
      const pages = [];
      const maxVisible = 7;

      if (totalPages.value <= maxVisible) {
        for (let i = 1; i <= totalPages.value; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage.value <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push("...");
          pages.push(totalPages.value);
        } else if (currentPage.value >= totalPages.value - 3) {
          pages.push(1);
          pages.push("...");
          for (let i = totalPages.value - 4; i <= totalPages.value; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push("...");
          for (let i = currentPage.value - 1; i <= currentPage.value + 1; i++) {
            pages.push(i);
          }
          pages.push("...");
          pages.push(totalPages.value);
        }
      }

      return pages;
    });

    // 创建点击涟漪效果
    const createRipple = (event) => {
      const button = event.currentTarget;
      const ripple = document.createElement("span");
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = event.clientX - rect.left - size / 2;
      const y = event.clientY - rect.top - size / 2;

      ripple.style.cssText = `
        position: absolute;
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(255, 255, 255, 0.8), transparent);
        left: ${x}px;
        top: ${y}px;
        pointer-events: none;
        transform: scale(0);
        animation: ripple-animation 0.6s ease-out;
        z-index: 1;
      `;

      button.appendChild(ripple);
      setTimeout(() => ripple.remove(), 600);
    };

    const goToPage = (page) => {
      if (page === "..." || page === currentPage.value) return;
      currentPage.value = page;
      emit("update:modelValue", page);
      emit("change", page);
    };

    const goToPrevious = () => {
      if (currentPage.value > 1) {
        goToPage(currentPage.value - 1);
      }
    };

    const goToNext = () => {
      if (currentPage.value < totalPages.value) {
        goToPage(currentPage.value + 1);
      }
    };

    const handleJump = () => {
      const page = parseInt(jumpPage.value);
      if (page >= 1 && page <= totalPages.value) {
        goToPage(page);
        jumpPage.value = "";
      }
    };

    watch(
      () => props.modelValue,
      (newVal) => {
        currentPage.value = newVal;
      }
    );

    return {
      currentPage,
      totalPages,
      startItem,
      endItem,
      visiblePages,
      jumpPage,
      goToPage,
      goToPrevious,
      goToNext,
      handleJump,
      createRipple,
    };
  },
};
</script>

<style scoped>
/* 涟漪动画 */
@keyframes ripple-animation {
  from {
    transform: scale(0);
    opacity: 1;
  }
  to {
    transform: scale(2);
    opacity: 0;
  }
}

/* 脉冲动画 */
@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

/* 数字翻转动画 */
@keyframes flip-in {
  from {
    transform: rotateX(-90deg);
    opacity: 0;
  }
  to {
    transform: rotateX(0);
    opacity: 1;
  }
}

/* 光晕扫描动画 */
@keyframes shine-sweep {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}

.pagination-wrapper {
  width: 100%;
  padding: 2.5rem 0;
  display: flex;
  justify-content: center;
}

.pagination-container {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.98),
    rgba(249, 250, 251, 0.98)
  );
  backdrop-filter: blur(16px);
  padding: 1rem 1.75rem;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.6);
  position: relative;
  overflow: visible;
}

/* 容器光晕效果 */
.pagination-container::before {
  content: "";
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, #667eea, #764ba2, #f093fb, #4facfe);
  border-radius: 20px;
  opacity: 0;
  z-index: -1;
  transition: opacity 0.3s ease;
  filter: blur(8px);
}

.pagination-container:hover::before {
  opacity: 0.3;
}

/* 分页信息 */
.pagination-info {
  display: flex;
  align-items: center;
}

.info-badge {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.35);
  position: relative;
  overflow: hidden;
}

/* 信息徽章光晕扫描 */
.info-badge::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shine-sweep 3s ease-in-out infinite;
}

.info-icon {
  width: 18px;
  height: 18px;
  color: white;
  flex-shrink: 0;
  animation: flip-in 0.6s ease-out;
}

.info-text {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: white;
}

.info-range {
  font-size: 1rem;
  animation: flip-in 0.6s ease-out;
}

.info-divider {
  opacity: 0.6;
}

.info-total {
  opacity: 0.9;
  animation: flip-in 0.6s ease-out 0.1s backwards;
}

/* 分页控件 */
.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* 导航按钮 */
.nav-btn {
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.nav-icon {
  width: 20px;
  height: 20px;
  color: #6b7280;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.nav-ripple {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #667eea, #764ba2);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.nav-btn:not(.disabled):hover {
  border-color: #667eea;
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.25);
}

.nav-btn:not(.disabled):hover .nav-ripple {
  opacity: 1;
}

.nav-btn:not(.disabled):hover .nav-icon {
  color: white;
}

.prev-btn:not(.disabled):hover .nav-icon {
  transform: translateX(-2px);
  animation: bounce-left 0.6s ease-in-out infinite;
}

.next-btn:not(.disabled):hover .nav-icon {
  transform: translateX(2px);
  animation: bounce-right 0.6s ease-in-out infinite;
}

@keyframes bounce-left {
  0%,
  100% {
    transform: translateX(-2px);
  }
  50% {
    transform: translateX(-6px);
  }
}

@keyframes bounce-right {
  0%,
  100% {
    transform: translateX(2px);
  }
  50% {
    transform: translateX(6px);
  }
}

.nav-btn.disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* 页码列表 */
.page-list {
  display: flex;
  gap: 0.375rem;
}

.page-btn {
  position: relative;
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.75rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.page-number {
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  animation: flip-in 0.4s ease-out;
}

.page-glow {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #667eea, #764ba2);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.active-bar {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 3px;
  background: linear-gradient(to right, #667eea, #764ba2);
  border-radius: 2px 2px 0 0;
  z-index: 3;
  animation: slide-up 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes slide-up {
  from {
    transform: translateX(-50%) translateY(10px);
    opacity: 0;
  }
  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

/* 活动页码脉冲环 */
.active-pulse {
  position: absolute;
  inset: -4px;
  border: 2px solid #667eea;
  border-radius: 12px;
  opacity: 0;
  animation: pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
}

.page-btn:not(.ellipsis):not(.active):hover {
  border-color: #667eea;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.2);
}

.page-btn:not(.ellipsis):not(.active):hover .page-glow {
  opacity: 0.1;
}

.page-btn:not(.ellipsis):not(.active):hover .page-number {
  color: #667eea;
  transform: scale(1.1);
}

.page-btn.active {
  border-color: #667eea;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
  transform: scale(1.05);
}

.page-btn.active .page-glow {
  opacity: 1;
}

.page-btn.active .page-number {
  color: white;
  transform: scale(1.1);
}

.page-btn.ellipsis {
  cursor: default;
  border-color: transparent;
  background: transparent;
  min-width: 32px;
}

/* 快速跳转 */
.quick-jump {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.jump-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
}

.jump-group {
  display: flex;
  gap: 0.375rem;
}

.jump-input {
  width: 60px;
  height: 40px;
  padding: 0 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
  color: #374151;
  transition: all 0.3s ease;
}

.jump-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  transform: scale(1.05);
}

.jump-input::placeholder {
  color: #9ca3af;
}

.jump-btn {
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  overflow: hidden;
}

.jump-icon {
  width: 18px;
  height: 18px;
  color: white;
  transition: transform 0.3s ease;
  position: relative;
  z-index: 2;
}

.jump-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.jump-btn:hover .jump-icon {
  transform: translateX(2px);
  animation: bounce-right 0.6s ease-in-out infinite;
}

/* 响应式 */
@media (max-width: 1024px) {
  .pagination-container {
    gap: 1rem;
    padding: 0.875rem 1.25rem;
  }

  .quick-jump {
    display: none;
  }
}

@media (max-width: 768px) {
  .pagination-container {
    gap: 0.75rem;
    padding: 0.75rem 1rem;
  }

  .info-badge {
    padding: 0.375rem 0.75rem;
  }

  .info-icon {
    width: 16px;
    height: 16px;
  }

  .info-text {
    font-size: 0.8125rem;
  }

  .nav-btn,
  .page-btn,
  .jump-input,
  .jump-btn {
    width: 36px;
    height: 36px;
    min-width: 36px;
  }

  .page-btn {
    padding: 0 0.5rem;
  }
}

@media (max-width: 640px) {
  .pagination-info {
    display: none;
  }
}
</style>
